import React, { useState } from 'react';
import axios from 'axios';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import PropTypes from 'prop-types';

/**
 * Componente reutilizable para generar un manifiesto de envíos
 * @param {Object} props - Las propiedades del componente
 * @param {Function} props.onClose - Función para cerrar el modal
 * @param {Function} props.onManifestGenerated - Función opcional que se ejecuta cuando se genera un manifiesto
 * @param {string} props.buttonText - Texto para el botón que abre el modal (opcional)
 * @param {Object} props.buttonStyle - Estilos para el botón que abre el modal (opcional)
 * @returns {JSX.Element} Componente ManifiestoGenerator
 */
const ManifiestoGenerator = ({ 
  onClose, 
  onManifestGenerated,
  buttonText = "Generar Manifiesto",
  buttonStyle = {}
}) => {
  const [showModal, setShowModal] = useState(false);
  const [manifestForm, setManifestForm] = useState({
    id_transportadora: '',
    fecha_inicio: new Date().toISOString().split('T')[0],
    fecha_fin: new Date().toISOString().split('T')[0]
  });
  const [manifestData, setManifestData] = useState(null);
  const [loading, setLoading] = useState(false);
  // Estado para controlar el modal de error
  const [errorModalVisible, setErrorModalVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Estilo predeterminado para el botón
  const defaultButtonStyle = {
    padding: '10px 20px',
    backgroundColor: '#4c45b1',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontWeight: 'bold'
  };

  // Combina los estilos predeterminados con los personalizados
  const finalButtonStyle = { ...defaultButtonStyle, ...buttonStyle };

  const openModal = () => {
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setManifestData(null);
    if (onClose) onClose();
  };

  // Función para mostrar el modal de error
  const showErrorModal = (message) => {
    setErrorMessage(message);
    setErrorModalVisible(true);
  };

  // Función para cerrar el modal de error
  const closeErrorModal = () => {
    setErrorModalVisible(false);
    setErrorMessage('');
  };

  const handleManifestSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const codigo_sucursal = localStorage.getItem('codigoSucursal');
      const requestData = {
        ...manifestForm,
        codigo_sucursal
      };
      
      const response = await axios.post('https://99envios.app/api/online/generar-manifiesto/pdf', requestData);
      
      // Verificar si la respuesta contiene un error en formato de string
      if (response.data && typeof response.data === 'string' && response.data.toLowerCase().includes('error')) {
        showErrorModal(response.data);
        setLoading(false);
        return;
      }
      
      // Verificar si la respuesta contiene una propiedad 'error'
      if (response.data && response.data.error) {
        showErrorModal(response.data.error);
        setLoading(false);
        return;
      }
      
      setManifestData(response.data);
      
      // Si se proporciona una función de devolución de llamada, ejecútala
      if (onManifestGenerated) {
        onManifestGenerated(response.data);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error generating manifest:', error);
      
      // Obtener y mostrar el mensaje de error de la API
      let errorMsg = 'Error al generar el manifiesto';
      if (error.response) {
        // Si hay una respuesta con datos de error
        if (error.response.data) {
          if (typeof error.response.data === 'string') {
            errorMsg = error.response.data;
          } else if (error.response.data.mensaje) {
            errorMsg = error.response.data.mensaje;
          } else if (error.response.data.error) {
            errorMsg = error.response.data.error;
          }
        }
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      showErrorModal(errorMsg);
      setLoading(false);
    }
  };

  const generatePDF = () => {
    if (!manifestData || !manifestData.datos_gias) return;
    
    try {
      // Create a new PDF document - explicitly add jsPDF options to avoid errors
      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      });
      
      // Add title
      doc.setFontSize(18);
      doc.setTextColor(76, 69, 177); // #4c45b1
      doc.text(`Manifiesto de ${manifestData.transportadora || 'Envíos'}`, 14, 15);
      
      // Add date
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      const today = new Date().toLocaleDateString('es-CO', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      doc.text(`Fecha: ${today}`, 14, 22);
      
      // Add transportadora info
      doc.setFontSize(12);
      doc.setTextColor(76, 69, 177);
      doc.text(`Transportadora: ${manifestData.transportadora || ''}`, 14, 29);
      
      // Enfoque más simplificado para dibujar la tabla
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 10;
      const startY = 40;
      let currentY = startY;
      const rowHeight = 10;
      
      // Definimos encabezados y anchos
      const columns = [
        { header: "Número", width: 25 },
        { header: "Fecha", width: 25 },
        { header: "Flete", width: 25 },
        { header: "Contrapago", width: 25 },
        { header: "Contenido", width: 50 },
        { header: "Destinatario", width: 40 },
        { header: "Teléfono", width: 30 },
        { header: "Peso", width: 20 }
      ];
      
      // Función para dibujar una fila
      const drawRow = (items, isHeader = false) => {
        let x = margin;
        
        // Configurar estilo según sea encabezado o fila de datos
        if (isHeader) {
          doc.setFillColor(76, 69, 177);
          doc.setTextColor(255, 255, 255);
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(8);
        } else {
          doc.setTextColor(0, 0, 0); // Asegurar que el texto sea negro
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(7);
        }
        
        // Dibujar cada celda en la fila
        columns.forEach((column, i) => {
          // Dibujar el rectángulo de fondo
          doc.rect(x, currentY, column.width, rowHeight, isHeader ? 'F' : 'S');
          
          // Calcular posición de texto
          let textX = x + 2; // Margen interno
          const textY = currentY + 6; // Centrado vertical aproximado
          
          // Si es un valor monetario, alinear a la derecha
          if ((i === 2 || i === 3) && !isHeader) {
            // Para valores monetarios, alineamos a la derecha
            const text = items[i];
            const textWidth = doc.getStringUnitWidth(text) * doc.internal.getFontSize() / doc.internal.scaleFactor;
            textX = x + column.width - 2 - textWidth;
          }
          
          // Escribir texto
          doc.text(items[i], textX, textY);
          x += column.width;
        });
        
        currentY += rowHeight;
        
        // Añadir nueva página si es necesario
        if (currentY > pageHeight - margin * 2) {
          doc.addPage();
          currentY = margin;
          
          // Si estamos en una nueva página, dibujamos el encabezado nuevamente
          if (!isHeader) {
            const headers = columns.map(col => col.header);
            drawRow(headers, true);
          }
        }
      };
      
      // Dibujar encabezado
      const headers = columns.map(col => col.header);
      drawRow(headers, true);
      
      // Dibujar filas de datos
      manifestData.datos_gias.forEach(guia => {
        try {
          const destinatario = guia.destinatario 
            ? `${guia.destinatario.nombre || ''} ${guia.destinatario.primer_apellido || ''}`.trim()
            : 'N/A';
          
          const row = [
            guia.numero_preenvio || 'N/A',
            (guia.fecha_creacion || '').substr(0, 10) || 'N/A',
            `$${parseFloat(guia.valor_flete || 0).toLocaleString('es-CO')}`,
            `$${parseFloat(guia.valor_contrapago || 0).toLocaleString('es-CO')}`,
            (guia.dice_contener || '').substr(0, 25) || 'N/A', // Limitamos longitud
            destinatario.substr(0, 20) || 'N/A', // Limitamos longitud
            (guia.destinatario?.telefono || 'N/A').substr(0, 12),
            `${guia.peso || 0} kg`
          ];
          
          drawRow(row, false);
        } catch (error) {
          console.error('Error al procesar fila:', error);
        }
      });
      
      // Añadir resumen al final
      currentY += 5; // Espacio después de la tabla
      
      doc.setFontSize(10);
      doc.setTextColor(50, 50, 50);
      doc.text(`Total Guías: ${manifestData.datos_gias.length}`, margin, currentY + 5);
      
      // Calculate totals safely with error handling
      let totalFlete = 0;
      let totalContrapago = 0;
      
      manifestData.datos_gias.forEach(guia => {
        try {
          totalFlete += parseFloat(guia.valor_flete || 0);
          totalContrapago += parseFloat(guia.valor_contrapago || 0);
        } catch (error) {
          // Skip problematic values
        }
      });
      
      doc.text(`Total Valor Flete: $${totalFlete.toLocaleString('es-CO')}`, margin, currentY + 12);
      doc.text(`Total Valor Contrapago: $${totalContrapago.toLocaleString('es-CO')}`, margin, currentY + 19);
      
      // Add footer
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.text(
          `Página ${i} de ${pageCount} - Generado por 99envios - ${today}`,
          doc.internal.pageSize.width / 2,
          doc.internal.pageSize.height - 10,
          { align: "center" }
        );
      }
      
      // Save the PDF with safer filename
      const safeName = manifestData.transportadora ? 
                        manifestData.transportadora.replace(/[^a-zA-Z0-9]/g, '_') : 
                        'Envios';
      doc.save(`Manifiesto_${safeName}_${new Date().toISOString().split('T')[0]}.pdf`);
      
    } catch (error) {
      console.error('Error detallado al generar el PDF:', error);
      showErrorModal('Error al generar el PDF: ' + (error.message || 'Error desconocido'));
    }
  };

  // Componente renderiza solo el botón si el modal no está abierto
  if (!showModal) {
    return (
      <button 
        onClick={openModal}
        style={finalButtonStyle}
      >
        {buttonText}
      </button>
    );
  }

  // Modal de error
  const ErrorModal = () => (
    <div className="modal-overlay" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0, 
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: errorModalVisible ? 'flex' : 'none',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1500
    }}>
      <div className="error-modal-content" style={{ 
        backgroundColor: 'white',
        padding: '25px',
        borderRadius: '12px',
        boxShadow: '0 5px 15px rgba(0, 0, 0, 0.3)',
        maxWidth: '450px',
        width: '90%',
        position: 'relative'
      }}>
        <button 
          onClick={closeErrorModal} 
          style={{ 
            position: 'absolute',
            top: '10px',
            right: '10px',
            background: 'none',
            border: 'none',
            fontSize: '24px',
            cursor: 'pointer',
            color: '#666'
          }}
        >
          &times;
        </button>
        
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          marginTop: '10px'
        }}>
          <div style={{
            width: '70px',
            height: '70px',
            borderRadius: '50%',
            backgroundColor: '#FEF0F0',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: '20px'
          }}>
            <span style={{ 
              color: '#DE3545', 
              fontSize: '50px', 
              fontWeight: 'bold',
              lineHeight: '1'
            }}>!</span>
          </div>
          
          <h3 style={{ 
            margin: '0 0 15px 0',
            color: '#333',
            fontSize: '20px',
            textAlign: 'center'
          }}>
            Error al Generar Manifiesto
          </h3>
          
          <p style={{ 
            color: '#666',
            textAlign: 'center',
            fontSize: '16px',
            marginBottom: '20px',
            lineHeight: '1.5'
          }}>
            {errorMessage}
          </p>
          
          <button 
            onClick={closeErrorModal}
            style={{ 
              padding: '12px 24px',
              backgroundColor: '#4c45b1',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              fontWeight: 'bold',
              marginTop: '10px'
            }}
          >
            Entendido
          </button>
        </div>
      </div>
    </div>
  );

  // Si el modal está abierto, renderiza el modal completo
  return (
    <>
      {/* Modal principal */}
      <div className="modal-overlay" style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0, 
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <div className="modal-content" style={{ 
          width: manifestData ? '85%' : '450px', 
          maxWidth: '1200px', 
          maxHeight: '90vh',
          overflow: 'auto',
          padding: '25px',
          borderRadius: '12px',
          boxShadow: '0 5px 15px rgba(0, 0, 0, 0.2)',
          backgroundColor: 'white'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h2 style={{ margin: 0, color: '#4c45b1' }}>
              {manifestData ? 'Manifiesto Generado' : 'Generar Manifiesto'}
            </h2>
            <button 
              onClick={closeModal} 
              style={{ 
                background: 'none', 
                border: 'none', 
                fontSize: '24px',
                cursor: 'pointer',
                color: '#666'
              }}
            >
              &times;
            </button>
          </div>

          {!manifestData ? (
            <form onSubmit={handleManifestSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                <label htmlFor="transportadora" style={{ fontWeight: 'bold' }}>Seleccionar Transportadora:</label>
                <select
                  id="transportadora"
                  value={manifestForm.id_transportadora}
                  onChange={(e) => setManifestForm({...manifestForm, id_transportadora: e.target.value})}
                  required
                  style={{ 
                    padding: '10px', 
                    borderRadius: '5px',
                    border: '1px solid #ddd'
                  }}
                >
                  <option value="">Seleccionar Transportadora</option>
                  <option value="1">Interrapidisimo</option>
                  <option value="2">Servientrega</option>
                  <option value="3">TCC</option>
                  <option value="4">Coordinadora</option>
                </select>
              </div>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                <label htmlFor="fecha_inicio" style={{ fontWeight: 'bold' }}>Fecha Inicio:</label>
                <input
                  id="fecha_inicio"
                  type="date"
                  value={manifestForm.fecha_inicio}
                  onChange={(e) => setManifestForm({...manifestForm, fecha_inicio: e.target.value})}
                  required
                  style={{ 
                    padding: '10px', 
                    borderRadius: '5px',
                    border: '1px solid #ddd'
                  }}
                />
              </div>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                <label htmlFor="fecha_fin" style={{ fontWeight: 'bold' }}>Fecha Fin:</label>
                <input
                  id="fecha_fin"
                  type="date"
                  value={manifestForm.fecha_fin}
                  onChange={(e) => setManifestForm({...manifestForm, fecha_fin: e.target.value})}
                  required
                  style={{ 
                    padding: '10px', 
                    borderRadius: '5px',
                    border: '1px solid #ddd'
                  }}
                />
              </div>
              
              <div className="modal-buttons" style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                gap: '10px',
                marginTop: '15px' 
              }}>
                <button 
                  type="submit" 
                  disabled={loading}
                  style={{ 
                    padding: '10px 20px',
                    backgroundColor: loading ? '#cccccc' : '#4c45b1',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: loading ? 'default' : 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  {loading ? 'Generando...' : 'Generar'}
                </button>
                <button 
                  type="button" 
                  onClick={closeModal}
                  style={{ 
                    padding: '10px 20px',
                    backgroundColor: '#f5f5f5',
                    color: '#333',
                    border: '1px solid #ddd',
                    borderRadius: '5px',
                    cursor: 'pointer'
                  }}
                >
                  Cancelar
                </button>
              </div>
            </form>
          ) : (
            <div className="manifest-results">
              <div className="manifest-header" style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: '20px',
                padding: '15px',
                borderRadius: '8px',
                backgroundColor: '#f8f9fa',
                border: '1px solid #e9ecef'
              }}>
                <div className="manifest-logos" style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                  {manifestData.logo_transportadora && (
                    <img 
                      src={manifestData.logo_transportadora} 
                      alt="Logo Transportadora" 
                      height="60" 
                      style={{ objectFit: 'contain' }}
                    />
                  )}
                  {manifestData.logo_99envios && (
                    <img 
                      src={manifestData.logo_99envios} 
                      alt="Logo 99envios" 
                      height="45" 
                      style={{ objectFit: 'contain' }}
                    />
                  )}
                </div>
                <h3 style={{ 
                  color: '#4c45b1', 
                  fontSize: '22px',
                  margin: '0'
                }}>
                  Manifiesto {manifestData.transportadora || 'de Envíos'}
                </h3>
                <div style={{ minWidth: '80px' }}></div> {/* Spacer for balance */}
              </div>
              
              {manifestData.datos_gias && manifestData.datos_gias.length > 0 ? (
                <div className="manifest-guias">
                  <h4 style={{ 
                    marginBottom: '15px',
                    color: '#333',
                    fontSize: '18px',
                    borderBottom: '2px solid #4c45b1',
                    paddingBottom: '10px'
                  }}>
                    Guías incluidas en el manifiesto
                  </h4>
                  <div className="table-wrapper" style={{ 
                    maxHeight: '50vh', 
                    overflowY: 'auto',
                    borderRadius: '8px',
                    border: '1px solid #e9ecef'
                  }}>
                    <table className="custom-table" style={{ 
                      width: '100%',
                      borderCollapse: 'collapse',
                      fontSize: '14px'
                    }}>
                      <thead style={{ position: 'sticky', top: 0, backgroundColor: '#4c45b1', color: 'white' }}>
                        <tr>
                          <th style={{ padding: '12px 15px', textAlign: 'left' }}>Número Preenvío</th>
                          <th style={{ padding: '12px 15px', textAlign: 'left' }}>Fecha Creación</th>
                          <th style={{ padding: '12px 15px', textAlign: 'right' }}>Valor Flete</th>
                          <th style={{ padding: '12px 15px', textAlign: 'right' }}>Valor Contrapago</th>
                          <th style={{ padding: '12px 15px', textAlign: 'left' }}>Dice Contener</th>
                          <th style={{ padding: '12px 15px', textAlign: 'right' }}>Valor Declarado</th>
                          <th style={{ padding: '12px 15px', textAlign: 'left' }}>Destinatario</th>
                          <th style={{ padding: '12px 15px', textAlign: 'left' }}>Teléfono</th>
                          <th style={{ padding: '12px 15px', textAlign: 'left' }}>Dirección</th>
                          <th style={{ padding: '12px 15px', textAlign: 'center' }}>Peso</th>
                        </tr>
                      </thead>
                      <tbody>
                        {manifestData.datos_gias.map((guia, index) => (
                          <tr key={index} style={{ 
                            backgroundColor: index % 2 === 0 ? '#f8f9fa' : 'white',
                            borderBottom: '1px solid #e9ecef'
                          }}>
                            <td style={{ padding: '10px 15px', textAlign: 'left' }}>{guia.numero_preenvio}</td>
                            <td style={{ padding: '10px 15px', textAlign: 'left' }}>{guia.fecha_creacion}</td>
                            <td style={{ padding: '10px 15px', textAlign: 'right' }}>${parseFloat(guia.valor_flete || 0).toLocaleString('es-CO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                            <td style={{ padding: '10px 15px', textAlign: 'right' }}>${parseFloat(guia.valor_contrapago || 0).toLocaleString('es-CO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                            <td style={{ padding: '10px 15px', textAlign: 'left' }}>{guia.dice_contener}</td>
                            <td style={{ padding: '10px 15px', textAlign: 'right' }}>${parseFloat(guia.valor_declarado || 0).toLocaleString('es-CO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                            <td style={{ padding: '10px 15px', textAlign: 'left' }}>{guia.destinatario ? `${guia.destinatario.nombre} ${guia.destinatario.primer_apellido || ''}` : 'N/A'}</td>
                            <td style={{ padding: '10px 15px', textAlign: 'left' }}>{guia.destinatario?.telefono || 'N/A'}</td>
                            <td style={{ padding: '10px 15px', textAlign: 'left' }}>{guia.destinatario?.direccion || 'N/A'}</td>
                            <td style={{ padding: '10px 15px', textAlign: 'center' }}>{guia.peso} kg</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  <div className="manifest-summary" style={{ 
                    marginTop: '20px',
                    padding: '15px',
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #e9ecef',
                    borderRadius: '8px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap'
                  }}>
                    <div style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      padding: '10px 15px',
                      borderRadius: '5px',
                      backgroundColor: '#e9ecef'
                    }}>
                      <strong style={{ marginRight: '10px' }}>Total Guías:</strong> 
                      <span style={{ fontSize: '18px', color: '#4c45b1' }}>{manifestData.datos_gias.length}</span>
                    </div>
                    
                    <div style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      padding: '10px 15px',
                      borderRadius: '5px',
                      backgroundColor: '#e9ecef'
                    }}>
                      <strong style={{ marginRight: '10px' }}>Total Valor Flete:</strong> 
                      <span style={{ fontSize: '18px', color: '#4c45b1' }}>
                        ${manifestData.datos_gias.reduce((sum, guia) => sum + parseFloat(guia.valor_flete || 0), 0).toLocaleString('es-CO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                    
                    <div style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      padding: '10px 15px',
                      borderRadius: '5px',
                      backgroundColor: '#e9ecef'
                    }}>
                      <strong style={{ marginRight: '10px' }}>Total Valor Contrapago:</strong> 
                      <span style={{ fontSize: '18px', color: '#4c45b1' }}>
                        ${manifestData.datos_gias.reduce((sum, guia) => sum + parseFloat(guia.valor_contrapago || 0), 0).toLocaleString('es-CO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ 
                  padding: '40px', 
                  textAlign: 'center',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  border: '1px solid #e9ecef'
                }}>
                  <p style={{ fontSize: '18px', color: '#6c757d' }}>No hay guías disponibles para este manifiesto.</p>
                </div>
              )}
              
              <div className="modal-buttons" style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                gap: '15px',
                marginTop: '25px' 
              }}>
                <button 
                  type="button" 
                  onClick={generatePDF}
                  style={{ 
                    padding: '12px 24px',
                    backgroundColor: '#217346', // Color verde de Excel
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <span style={{ fontSize: '18px' }}>⬇️</span> Descargar PDF
                </button>
                <button 
                  type="button" 
                  onClick={closeModal}
                  style={{ 
                    padding: '12px 24px',
                    backgroundColor: '#f5f5f5',
                    color: '#333',
                    border: '1px solid #ddd',
                    borderRadius: '5px',
                    cursor: 'pointer'
                  }}
                >
                  Cerrar
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal de error */}
      <ErrorModal />
    </>
  );
};

ManifiestoGenerator.propTypes = {
  onClose: PropTypes.func,
  onManifestGenerated: PropTypes.func,
  buttonText: PropTypes.string,
  buttonStyle: PropTypes.object
};

export default ManifiestoGenerator;